import { useEffect, useState } from "react";

import { useEditorView } from "@remirror/react";

const getEditorChromeRoot = (editorDom: HTMLElement | null) =>
  editorDom?.parentNode?.parentNode?.parentNode ?? null;

const isWithinEditorChrome = (
  target: EventTarget | null,
  editorDom: HTMLElement,
) => {
  if (!target || !(target instanceof Node)) {
    return false;
  }

  const editorRoot = getEditorChromeRoot(editorDom);
  const modalMount = document.getElementById("semantic-modal-mount-node");

  return !!editorRoot?.contains(target) || !!modalMount?.contains(target);
};

export const useFocusState = () => {
  const view = useEditorView();
  const [isFocused, setIsFocused] = useState(() =>
    isWithinEditorChrome(document.activeElement, view.dom),
  );

  useEffect(() => {
    const syncFocusState = (target?: EventTarget | null) => {
      const nextTarget = target ?? document.activeElement;
      setIsFocused(isWithinEditorChrome(nextTarget, view.dom));
    };

    const onEditorFocus = () => {
      setIsFocused(true);
    };

    const onFocusIn = (e: FocusEvent) => {
      if (isWithinEditorChrome(e.target, view.dom)) {
        setIsFocused(true);
      }
    };

    let lastPointerTarget: EventTarget | null = null;
    const onPointerDown = (e: PointerEvent) => {
      lastPointerTarget = e.target;
    };

    const onFocusOut = (e: FocusEvent) => {
      const relatedTarget =
        e.relatedTarget ||
        (e as FocusEvent & { explicitOriginalTarget?: EventTarget })
          .explicitOriginalTarget ||
        lastPointerTarget;
      lastPointerTarget = null;

      // Defer until the browser has applied the next focus target.
      requestAnimationFrame(() => {
        syncFocusState(relatedTarget);
      });
    };

    view.dom.addEventListener("focus", onEditorFocus);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", onFocusOut, true);
    document.addEventListener("pointerdown", onPointerDown, { capture: true });

    return () => {
      view.dom.removeEventListener("focus", onEditorFocus);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", onFocusOut, true);
      document.removeEventListener("pointerdown", onPointerDown, {
        capture: true,
      });
    };
  }, [view.dom]);

  return isFocused;
};
